/**
 * Message filtering for the Data Stream, scoped to the selected topic.
 *
 * There is deliberately no query *language*. A filter is produced either by
 * clicking a value in the JSON pane or by picking a field from the structure
 * we already learned from that topic's messages (see discoverFields). The UI
 * never asks the user to know a path, a nesting level, or an operator — this
 * module is only the representation those choices compile down to.
 */
import type { RawMqttMessage } from '@/lib/mqtt/types';

/** Comparison a filter applies. Which of these are offered depends on the
 *  field's observed type — the user never sees an operator that can't work. */
export type FieldOp = 'is' | 'not' | 'contains' | 'above' | 'below';

export type FilterValue = string | number | boolean | null;

export interface FieldFilter {
  /** `path|op|value` — stable React key and dedupe identity. */
  id: string;
  /** Dotted path into the payload. `[]` matches any element of an array, so
   *  `targets.[].hex` means "some target has this hex". */
  path: string;
  op: FieldOp;
  value: FilterValue;
}

export function makeFieldFilter(path: string, op: FieldOp, value: FilterValue): FieldFilter {
  return { id: `${path}|${op}|${String(value)}`, path, op, value };
}

/** Last meaningful segment of a dotted path. Array markers and indices are
 *  skipped so 'targets.[].hex' reads as 'hex' — what the user pointed at. */
export function fieldLabel(path: string): string {
  const named = path.split('.').filter((p) => p !== '[]' && !/^\d+$/.test(p));
  return named[named.length - 1] ?? path;
}

const OP_WORD: Record<FieldOp, string> = {
  is: 'is',
  not: 'is not',
  contains: 'contains',
  above: '>',
  below: '<',
};

/** Chip text. Plain English, no path noise — 'serial is SIMTL80'. */
export function describeFilter(f: FieldFilter): string {
  return `${fieldLabel(f.path)} ${OP_WORD[f.op]} ${f.value === null ? 'null' : String(f.value)}`;
}

/**
 * Every value a path resolves to. Usually one, but a `[]` segment fans out
 * across array elements — a filter on `targets.[].hex` then means "some
 * element matches", which is what a user pointing at a list intends.
 */
export function resolvePath(root: unknown, path: string): unknown[] {
  let cur: unknown[] = [root];
  for (const seg of path.split('.')) {
    const next: unknown[] = [];
    for (const node of cur) {
      if (node === null || typeof node !== 'object') continue;
      if (seg === '[]') {
        if (Array.isArray(node)) next.push(...node);
        continue;
      }
      const v = (node as Record<string, unknown>)[seg];
      if (v !== undefined) next.push(v);
    }
    if (next.length === 0) return [];
    cur = next;
  }
  return cur;
}

/** Values are compared by string form as well as identity: a filter typed as
 *  "4210" must still match a payload reporting the number 4210. */
function looseEq(a: unknown, b: FilterValue): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a === 'object') return false;
  return String(a) === String(b);
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function compare(actual: unknown, f: FieldFilter): boolean {
  switch (f.op) {
    case 'is':
    case 'not':
      return looseEq(actual, f.value);
    case 'contains':
      return String(actual).toLowerCase().includes(String(f.value).toLowerCase());
    case 'above': {
      const a = toNumber(actual);
      const b = toNumber(f.value);
      return a !== null && b !== null && a > b;
    }
    case 'below': {
      const a = toNumber(actual);
      const b = toNumber(f.value);
      return a !== null && b !== null && a < b;
    }
    default: {
      const exhaustive: never = f.op;
      return exhaustive;
    }
  }
}

export function matchesFieldFilter(payload: unknown, f: FieldFilter): boolean {
  const found = resolvePath(payload, f.path);
  // `not` is the only negative form: no resolved value may equal the target,
  // and a message missing the field entirely counts as "not that value".
  if (f.op === 'not') return !found.some((v) => compare(v, f));
  return found.some((v) => compare(v, f));
}

/** Drop every whitespace character. Used to compare text copied out of the
 *  pretty-printed JSON pane against the compact wire payload. */
export function compact(s: string): string {
  return s.replace(/\s+/g, '');
}

/**
 * Free-text match over a message's raw payload — the Ctrl+F model, scoped to
 * the topic the user is already looking at.
 *
 * Matching is whitespace-insensitive. The detail pane pretty-prints
 * (`"geodetic_altitude": 698.85`) while the wire payload is compact
 * (`"geodetic_altitude":698.85`), so a fragment copied from the pane would
 * otherwise never match the message it was copied from. Only queries that
 * actually contain whitespace pay for the second pass — which also scopes the
 * leniency to the direction that occurs in practice (spaced query, compact
 * payload); a hand-typed compact query against a pretty-printed payload still
 * needs to match literally.
 */
export function matchesText(msg: RawMqttMessage, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const raw = msg.raw.toLowerCase();
  if (raw.includes(q)) return true;
  if (!/\s/.test(q)) return false;
  return compact(raw).includes(compact(q));
}

export function matchesQuery(
  msg: RawMqttMessage,
  query: string,
  filters: readonly FieldFilter[],
): boolean {
  if (!matchesText(msg, query)) return false;
  for (const f of filters) {
    if (!matchesFieldFilter(msg.payload, f)) return false;
  }
  return true;
}

/** True when anything is narrowing the message list. */
export function isQueryActive(query: string, filters: readonly FieldFilter[]): boolean {
  return query.trim().length > 0 || filters.length > 0;
}

/** Strings to highlight in message-row previews: the free-text query plus the
 *  literal value of every equality/contains filter. */
export function highlightTerms(query: string, filters: readonly FieldFilter[]): string[] {
  const terms: string[] = [];
  const q = query.trim();
  if (q) terms.push(q);
  for (const f of filters) {
    if ((f.op !== 'is' && f.op !== 'contains') || f.value === null) continue;
    const v = String(f.value);
    if (v) terms.push(v);
  }
  return [...new Set(terms)];
}
